import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { api } from "../../lib/api.js";
import { Alert, Bolt, Check, Info, RefreshCw, Video } from "../../components/icons.js";
import type { useMicLevel } from "./useMicLevel.js";

/**
 * Candidate · Step 3 hardware diagnostics — REAL, client-side only, NOTHING persisted or sent
 * to the backend beyond the /health pings used for the latency read. Pure troubleshooting
 * aid: camera/mic status come from the caller's own hooks (shared with the identity-check UI
 * above it so we don't open two camera/mic prompts), browser compatibility is feature-detected,
 * network latency is a real timed round trip.
 *
 * ADVISORY ONLY. Nothing here gates the interview — no caller reads these results, and a candidate
 * on a poor line can always continue. So the wording must never imply a pass/fail: only a genuinely
 * unreachable API is an "Issue", and a merely slow connection (every candidate outside our Indian
 * region clears 400ms just by being far away) is reported as information, not a verdict.
 */

interface BrowserCheck {
  label: string;
  ok: boolean;
}

function detectBrowser(): BrowserCheck {
  if (typeof navigator === "undefined") return { label: "Unknown browser", ok: false };
  const ua = navigator.userAgent;
  const hasWebRTC = typeof RTCPeerConnection !== "undefined";
  const hasMedia = !!navigator.mediaDevices?.getUserMedia;
  const m =
    /Edg\/([\d.]+)/.exec(ua) ??
    /Chrome\/([\d.]+)/.exec(ua) ??
    /Firefox\/([\d.]+)/.exec(ua) ??
    /Version\/([\d.]+).*Safari/.exec(ua);
  const name = /Edg\//.test(ua)
    ? "Edge"
    : /Chrome\//.test(ua)
      ? "Chrome"
      : /Firefox\//.test(ua)
        ? "Firefox"
        : /Safari\//.test(ua)
          ? "Safari"
          : "Browser";
  const version = m?.[1]?.split(".")[0] ?? "";
  return {
    label: `${name}${version ? ` v${version}` : ""} · WebRTC ${hasWebRTC && hasMedia ? "active" : "unavailable"}`,
    ok: hasWebRTC && hasMedia,
  };
}

/** Samples per reading. Three is the smallest set with a meaningful middle value. */
const NET_SAMPLES = 3;

/**
 * Round-trip latency to the API, reported as the MEDIAN of three timed /health calls.
 *
 * A single sample was a coin flip: one request that happened to queue behind a TLS handshake or a
 * sleeping radio labelled a perfectly good connection as broken. The median discards exactly that
 * outlier. The calls are sequential, not parallel — three in flight at once would queue behind each
 * other and measure our own burst rather than the candidate's line.
 */
function useNetworkLatency(): {
  ms: number | null;
  taken: number;
  testing: boolean;
  retest: () => void;
} {
  const [ms, setMs] = useState<number | null>(null);
  const [taken, setTaken] = useState(0);
  // Starts true: a run always begins on mount, and a default of false would flash "no answer" for
  // the frame before the effect fires.
  const [testing, setTesting] = useState(true);
  // Identifies the current run so a "Re-detect" mid-flight can't have the abandoned run write its
  // stale median over the new one.
  const runRef = useRef(0);

  const retest = useCallback(() => {
    const run = ++runRef.current;
    setTesting(true);
    setTaken(0);
    void (async () => {
      const samples: number[] = [];
      for (let i = 0; i < NET_SAMPLES; i++) {
        const started = performance.now();
        try {
          await api.get("/health");
          samples.push(performance.now() - started);
        } catch {
          // A dropped sample is not a verdict — the surviving ones still yield a median.
        }
        if (runRef.current !== run) return;
        setTaken(i + 1);
      }
      if (runRef.current !== run) return;
      samples.sort((a, b) => a - b);
      setMs(samples.length > 0 ? Math.round(samples[Math.floor(samples.length / 2)]!) : null);
      setTesting(false);
    })();
  }, []);

  useEffect(() => {
    retest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { ms, taken, testing, retest };
}

/**
 * How a latency reading is described to the candidate. Only an API we could not reach at all is a
 * problem they should act on; slow is stated plainly and left at that, because distance from our
 * region is not something a candidate can fix and does not stop the interview.
 */
function netReading(
  ms: number | null,
  testing: boolean,
  taken: number,
): { detail: string; cls: string; label: string } {
  if (testing)
    return {
      detail: `Timing ${NET_SAMPLES} round trips — ${Math.min(taken + 1, NET_SAMPLES)} of ${NET_SAMPLES}…`,
      cls: "pill n",
      label: "Checking…",
    };
  if (ms === null)
    return {
      detail: `Couldn't reach us on any of ${NET_SAMPLES} tries — check your connection, then re-detect`,
      cls: "pill r",
      label: "No answer",
    };
  const median = `${ms}ms · median of ${NET_SAMPLES}`;
  if (ms < 150) return { detail: `${median} · excellent`, cls: "pill v", label: "OK" };
  if (ms < 400) return { detail: `${median} · comfortable`, cls: "pill v", label: "OK" };
  if (ms < 900)
    return {
      detail: `${median} · a little slower than average, fine for the interview`,
      cls: "pill n",
      label: "Usable",
    };
  return {
    detail: `${median} · slow, so expect a short pause before Asha replies — the interview still runs`,
    cls: "pill n",
    label: "Usable",
  };
}

/** Short, genuinely audible test tone via Web Audio — proves the speaker path actually works. */
function playTestTone(): void {
  try {
    const AudioCtx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = 440;
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.55);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.6);
    osc.onended = () => void ctx.close().catch(() => undefined);
  } catch {
    /* no-op — speaker test is best-effort */
  }
}

function DiagRow({
  icon,
  label,
  detail,
  ok,
  pending,
  status,
}: {
  icon: ReactNode;
  label: string;
  detail: string;
  ok: boolean;
  pending?: boolean;
  /** Overrides the binary OK/Issue pill for checks that have a middle state worth naming. */
  status?: { cls: string; label: string };
}): ReactNode {
  return (
    <div className={`cx-diag-row ${pending ? "" : ok ? "ok" : "issue"}`}>
      {/* Status-specific glyph (audit 31): a tick when it's fine, an alert when it isn't. */}
      <span className="ico">{pending ? icon : ok ? <Check /> : <Alert />}</span>
      <div>
        <div className="t">{label}</div>
        <div className="d">{detail}</div>
      </div>
      <span className="stat">
        {status ? (
          <span className={status.cls}>{status.label}</span>
        ) : pending ? (
          <span className="pill n">Checking…</span>
        ) : ok ? (
          <span className="pill v">OK</span>
        ) : (
          <span className="pill r">Issue</span>
        )}
      </span>
    </div>
  );
}

export function HardwareDiagnostics({
  camActive,
  camDenied,
  mic,
}: {
  camActive: boolean;
  camDenied: boolean;
  mic: ReturnType<typeof useMicLevel>;
}): ReactNode {
  const browser = detectBrowser();
  const net = useNetworkLatency();
  const startedMicRef = useRef(false);

  useEffect(() => {
    if (!startedMicRef.current) {
      startedMicRef.current = true;
      mic.start();
    }
    // Mic stream is closed by useMicLevel's own unmount cleanup — nothing to do here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const micBars = 14;
  const activeBars = Math.round(mic.level * micBars);

  const netReport = netReading(net.ms, net.testing, net.taken);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <h3 className="t1c-h3" style={{ margin: 0 }}>Device check</h3>
        <button
          className="btn sm ghost"
          onClick={() => {
            mic.stop();
            mic.start();
            net.retest();
          }}
        >
          <RefreshCw /> Re-check
        </button>
      </div>

      <DiagRow icon={<Video />} label="Webcam video feed" detail={camActive ? "Camera stream live" : camDenied ? "No camera access" : "Starting…"} ok={camActive} />

      <div className="cx-diag-row" style={{ alignItems: "center" }}>
        <span className="ico">
          <Bolt />
        </span>
        <div style={{ flex: 1 }}>
          <div className="t">Microphone & voice level</div>
          <div className="cx-mic-meter" style={{ marginTop: 6 }}>
            {Array.from({ length: micBars }).map((_, i) => (
              <span
                key={i}
                className="bar"
                style={{ height: `${8 + i * 0.9}px`, opacity: i < activeBars ? 1 : 0.25 }}
              />
            ))}
          </div>
        </div>
        <span className="stat">
          <button className="btn sm ghost" onClick={playTestTone}>
            Test speaker
          </button>
        </span>
      </div>

      <DiagRow icon={<Info />} label="Browser compatibility" detail={browser.label} ok={browser.ok} />
      {/* "latency", not "latency & speed": this is a round-trip timing, and we measure no
          throughput whatsoever — claiming a speed test would be inventing a number. */}
      <DiagRow
        icon={<Info />}
        label="Network latency"
        detail={netReport.detail}
        ok={net.testing || net.ms !== null}
        status={netReport}
      />
    </div>
  );
}
